import React, { useState } from 'react'
import { X, Send, Plus, Trash2, ExternalLink } from 'lucide-react'
import { URLValidator } from '@/utils/urlValidator'

interface WorkSubmissionModalProps {
    isOpen: boolean
    onClose: () => void
    onSubmit: (data: { deliveryUrls: string[], description: string }) => Promise<void>
    gigTitle: string
    isLoading?: boolean
}

const WorkSubmissionModal: React.FC<WorkSubmissionModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    gigTitle,
    isLoading = false
}) => {
    const [deliveryUrls, setDeliveryUrls] = useState<string[]>([''])
    const [description, setDescription] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [urlErrors, setUrlErrors] = useState<string[]>([])

    const handleAddUrl = () => {
        setDeliveryUrls([...deliveryUrls, ''])
        setUrlErrors([...urlErrors, ''])
    }

    const handleRemoveUrl = (index: number) => {
        const newUrls = deliveryUrls.filter((_, i) => i !== index)
        const newErrors = urlErrors.filter((_, i) => i !== index)
        setDeliveryUrls(newUrls.length > 0 ? newUrls : [''])
        setUrlErrors(newErrors.length > 0 ? newErrors : [''])
    }

    const handleUrlChange = (index: number, value: string) => {
        const newUrls = [...deliveryUrls]
        newUrls[index] = value
        setDeliveryUrls(newUrls)

        // Validate URL
        const newErrors = [...urlErrors]
        if (value.trim() && !URLValidator.isProbablySafeUrl(value.trim())) {
            newErrors[index] = 'Please enter a valid URL from supported platforms (GitHub, portfolio sites, etc.)'
        } else {
            newErrors[index] = ''
        }
        setUrlErrors(newErrors)
    }

    const validateForm = () => {
        const validUrls = deliveryUrls.filter(url => url.trim() !== '')
        const hasValidUrls = validUrls.length > 0 && validUrls.every(url => URLValidator.isProbablySafeUrl(url))
        const hasDescription = description.trim().length > 0
        const hasErrors = urlErrors.some(error => error !== '')

        return hasValidUrls && hasDescription && !hasErrors
    }

    const handleSubmit = async () => {
        if (!validateForm() || isSubmitting) return

        const validUrls = deliveryUrls.filter(url => url.trim() !== '')

        setIsSubmitting(true)
        try {
            await onSubmit({
                deliveryUrls: validUrls,
                description: description.trim()
            })
            // Reset form
            setDeliveryUrls([''])
            setDescription('')
            setUrlErrors([])
            onClose()
        } catch (error) {
            console.error('Failed to submit work:', error)
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault()
            if (validateForm()) {
                handleSubmit()
            }
        }
    }

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg max-w-2xl w-full mx-4 border border-gray-200 dark:border-gray-700 overflow-hidden max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 border-b border-gray-200 dark:border-gray-700">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Submit Work
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {gigTitle}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1 rounded hover:opacity-80 transition-opacity text-gray-600 dark:text-gray-400"
                        aria-label="Close submission modal"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Form content */}
                <div className="p-6 space-y-6">
                    {/* Delivery URLs */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                            Delivery URLs <span className="text-red-500">*</span>
                        </label>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                            Share links to your completed work (GitHub repos, live demos, portfolios, etc.)
                        </p>

                        <div className="space-y-3">
                            {deliveryUrls.map((url, index) => (
                                <div key={index} className="flex gap-2">
                                    <div className="flex-1">
                                        <div className="relative">
                                            <input
                                                type="url"
                                                value={url}
                                                onChange={(e) => handleUrlChange(index, e.target.value)}
                                                placeholder="https://github.com/username/project"
                                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:focus:ring-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors ${
                                                    urlErrors[index]
                                                        ? 'border-red-300 dark:border-red-600'
                                                        : 'border-gray-300 dark:border-gray-600'
                                                }`}
                                                disabled={isSubmitting || isLoading}
                                            />
                                            {url && URLValidator.isProbablySafeUrl(url) && (
                                                <a
                                                    href={url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                                                >
                                                    <ExternalLink size={16} />
                                                </a>
                                            )}
                                        </div>
                                        {urlErrors[index] && (
                                            <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                                                {urlErrors[index]}
                                            </p>
                                        )}
                                    </div>

                                    {deliveryUrls.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveUrl(index)}
                                            className="p-2 text-red-500 hover:text-red-700 dark:hover:text-red-400 transition-colors"
                                            disabled={isSubmitting || isLoading}
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>

                        <button
                            type="button"
                            onClick={handleAddUrl}
                            className="flex items-center gap-2 mt-3 px-3 py-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={isSubmitting || isLoading}
                        >
                            <Plus size={16} />
                            Add another URL
                        </button>
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                            Work Description <span className="text-red-500">*</span>
                        </label>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                            Describe what you've delivered and any important details about the work
                        </p>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Describe the work you've completed, key features, technologies used, and any special instructions for the client..."
                            rows={6}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg resize-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:focus:ring-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors"
                            disabled={isSubmitting || isLoading}
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            Press Ctrl+Enter to submit quickly
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                    <button
                        onClick={onClose}
                        disabled={isSubmitting || isLoading}
                        className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={!validateForm() || isSubmitting || isLoading}
                        className="flex items-center gap-2 px-6 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 dark:disabled:bg-gray-600 text-white rounded-lg disabled:cursor-not-allowed transition-colors font-medium"
                    >
                        {isSubmitting ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                            <Send size={16} />
                        )}
                        Submit Work
                    </button>
                </div>
            </div>
        </div>
    )
}

export default WorkSubmissionModal